import React, { useMemo } from 'react'
import type { RacePageDTO } from '@mobi/api-types'
import type { BetSource } from '@mobi/betslip/types'
import { BettableTip } from '@core/Areas/Racing/Components/BettableTips/BettableTip'
import {
  getAcceptorsFromTips,
  getPriceAndScratchings,
} from '@core/Areas/Racing/Components/BettableTips/helpers'
import type { RawTips } from '@core/Areas/Racing/Components/BettableTips/types'
import { useAcceptors } from '@core/Areas/Racing/Hooks/useAcceptors'
import { RaceListEntry } from '../../Hooks/useRaceList/types'
import { TipButtonsContainerStyled } from './BettableTips.styles'

export type Props = {
  tips: RawTips
  raceKey: string
  isRaceOpen: boolean
  tipSource: BetSource
  race?: RacePageDTO
  fixedRace?: RaceListEntry
  acceptorsLimit?: number
  author?: string
}

export const BettableTips = ({
  tips,
  isRaceOpen,
  race,
  tipSource,
  acceptorsLimit,
  author,
}: Props): Nullable<JSX.Element> => {
  const acceptors = useAcceptors(race)

  const selectedAcceptors = useMemo(
    () => getAcceptorsFromTips(tips, acceptors ?? new Map()),
    [tips, acceptors]
  )

  if (race === undefined || selectedAcceptors.length < 1) {
    return null
  }

  const displayedAcceptors = acceptorsLimit
    ? selectedAcceptors.slice(0, acceptorsLimit)
    : selectedAcceptors

  return (
    <TipButtonsContainerStyled
      data-testid={tipSource === 'tips' ? 'bettable-tips' : 'preview-tips'}
    >
      {displayedAcceptors.map((acceptor, i) => (
        <BettableTip
          key={acceptor.number}
          buttonPosition={i + 1}
          meeting={race.MeetingInformation}
          acceptor={acceptor}
          isRaceOpen={isRaceOpen}
          tipSource={tipSource}
          {...getPriceAndScratchings(acceptor)}
          author={author}
        />
      ))}
    </TipButtonsContainerStyled>
  )
}
